import React, { useState, useContext } from "react";
import { useRetrieveAllTiktokSellerAccounts } from "@/hooks/tiktokSellerAccount.hooks";
import { _Button } from "@/components/button";
import { MyAccountDropdown } from "@/components/my-account-dropdown";
import { ModeToggle } from "@/components/mode-toggle";
import { LoadingFallback } from "@/components/loading-fallback";
import {
  CircleXIcon,
  ShoppingBagIcon,
  ChevronDownIcon,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Loader2,
  PlusIcon,
  XCircleIcon,
} from "lucide-react";
import { UserStateContext } from "@/contexts/UserStateProvider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { MergoioLogo } from "@/components/layout/MergoioLogo";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { OrderCount } from "@/components/order-count";
import { AuthorizationExpiredModal } from "@/components/authorization-expired-modal";
import moment from "moment";
import { useToast } from "@/hooks/toast.hooks";
import { useTiktokService } from "@/hooks/tiktok.hooks";

export const SellerAccountPortal = () => {
  const [authorizationExpiredModalOpen, setAuthorizationExpiredModalOpen] =
    useState(false);
  const { buildAuthorizeUrl } = useTiktokService();
  const {
    data: sellerAccounts,
    isLoading,
    error,
  } = useRetrieveAllTiktokSellerAccounts();
  const { toast } = useToast();
  const { setSelectedSellerAccountId } = useContext(UserStateContext);
  const [actingBySignUpWithTiktok, setActingBySignUpWithTiktok] =
    useState(false);
  const handleAccountSelect = (accountId: string) => {
    const sellerAccount = sellerAccounts?.find(
      (account) => account.id.toString() === accountId
    );
    if (
      sellerAccount?.refreshTokenExpiresAt &&
      moment(sellerAccount.refreshTokenExpiresAt).isSameOrBefore(moment())
    ) {
      setAuthorizationExpiredModalOpen(true);
    } else {
      const id = Number(accountId);
      setSelectedSellerAccountId(id);
    }
  };

  const addNewSellerAccount = async () => {
    setActingBySignUpWithTiktok(true);
    setTimeout(async () => {
      const response = await buildAuthorizeUrl({
        isUS: false,
        state: {
          type: "ADD_SELLER_ACCOUNT",
        },
      });
      if (response.success && response.content.authorizeUrl) {
        window.location.href = response.content.authorizeUrl;
      } else {
        setActingBySignUpWithTiktok(false);
        toast({
          icon: <XCircleIcon size={12} className={"text-red-500"} />,
          variant: "destructive",
          title: "Uh oh! Something went wrong.",
          description: "Failed to generate TikTok authorization URL",
        });
      }
    }, 500);
  };

  return (
    <div className="flex flex-col items-center gap-4 justify-center min-h-screen bg-background p-4">
      {authorizationExpiredModalOpen && (
        <AuthorizationExpiredModal
          open={authorizationExpiredModalOpen}
          setOpen={setAuthorizationExpiredModalOpen}
          trigger={<></>}
          onSuccess={() => {}}
        />
      )}
      <div className="w-full max-w-xl flex flex-col gap-2 justify-start">
        <MergoioLogo width={200} />
        <OrderCount displayUpgradeButton={true} />
      </div>
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-1">
          <div className="flex justify-between gap-10 items-center">
            <CardDescription>
              Choose the Seller Account you want to manage...
            </CardDescription>
            <div className="flex space-x-2">
              <ModeToggle />
              <MyAccountDropdown />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingFallback loading={isLoading}>
            {error ? (
              <div className="bg-destructive/10 rounded-lg px-3 py-2 text-sm flex items-center text-destructive">
                <CircleXIcon size={16} className="mr-2" />
                {error.message}
              </div>
            ) : (
              <div className="space-y-4">
                {sellerAccounts?.map((account) => (
                  <div
                    key={account.id}
                    className="bg-muted/40 flex gap-2 items-center p-4 border rounded-lg cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => handleAccountSelect(account.id.toString())}
                  >
                    <ShoppingBagIcon className="mr-4 h-6 w-6 text-muted-foreground" />
                    <div className="lg:w-1/3 w-full break-words">
                      <p className="font-medium">{account.sellerName}</p>
                      <p className="text-sm text-muted-foreground">
                        {account.sellerBaseRegion}
                      </p>
                    </div>
                    <div className="lg:w-2/3 w-full flex items-center justify-end gap-2">
                      <Badge
                        className={cn(
                          account.refreshTokenExpiresAt &&
                            new Date(account.refreshTokenExpiresAt) <=
                              new Date()
                            ? "bg-red-500/10 text-red-500"
                            : account.setupOperationsCompleted
                            ? "bg-green-500/10 text-green-500"
                            : account.operations?.length > 0
                            ? "bg-blue-500/10 text-blue-500"
                            : "bg-orange-500/10 text-orange-500",
                          "flex items-center gap-2"
                        )}
                      >
                        {account.refreshTokenExpiresAt &&
                        new Date(account.refreshTokenExpiresAt) <=
                          new Date() ? (
                          <>
                            <AlertCircle className="h-4 w-4" />
                            Authorization Expired
                          </>
                        ) : account.setupOperationsCompleted ? (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            Setup
                          </>
                        ) : account.operations?.length > 0 ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Setting up
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-4 w-4" />
                            Setup Incomplete
                          </>
                        )}
                      </Badge>
                      <ChevronRight className="ml-2 h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                ))}

                <_Button
                  loading={actingBySignUpWithTiktok}
                  variant="outline"
                  className="w-full h-[60px] flex gap-2 items-center justify-center"
                  onClick={() => addNewSellerAccount()}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Seller Account
                </_Button>
              </div>
            )}
          </LoadingFallback>
        </CardContent>
      </Card>
    </div>
  );
};
